import Hero from "./Hero";
import useSlideAnimation from "../Hooks/useSlideAnimation";
import useMediaQuery from "../Hooks/useMediaQuery";
import React from 'react';

const PlanCard = ({ number, title, content }) => {
  return (
    <div className="flex flex-col gap-2 w-full text-left">
      <div className="font-hero text-4xl opacity-50 text-light-primary-text dark:text-dark-primary-text">
        {number}
      </div>
      <h3 className="hero3 font-hero mt-6 text-light-primary-text dark:text-dark-primary-text">
        {title}
      </h3>
      <div className="font-body text-md text-light-secondary-text dark:text-dark-secondary-text">
        {content}
      </div>
    </div>
  );
};

const ExperienceCard = ({ width, companyName, role, duration }) => {
  const breakPoint = useMediaQuery(768);
  return (
    <div
      style={{ width: !breakPoint ? width : "100%" }}
      className={`h-auto py-3 border-2 border-light-primary-text dark:border-none dark:bg-dark-primary-text text-light-primary-text dark:text-[#000] self-center rounded-full flex flex-col items-center justify-center gap-2`}
    >
      <div className="font-hero font-bold text-3xl">{companyName}</div>
      <div
        className="flex flex-row gap-2 font-body text-sm opacity-75"
        style={{ fontSize: "clamp(1rem, .9617rem + .1633vw, 1.125rem)" }}
      >
        <div>{role}</div>
        <div>•</div>
        <div>{duration}</div>
      </div>
    </div>
  );
};

const Work = () => {
  return (
    <div className="w-full my-10 h-auto flex flex-col gap-1 px-5 md:px-0">
      <ExperienceCard
        width="70%"
        companyName="RELX, USA"
        role="Software Engineer Intern"
        duration="June 2024 - Present"
      />
      <ExperienceCard
        width="60%"
        companyName="CSC Department: NCSU"
        role="Research Programmer"
        duration="Novemeber 2023 - May 2024"
      />
      <ExperienceCard
        width="50%"
        companyName="Hewlett Packard Enterprise"
        role="Software Engineer - Cloud R&D"
        duration="2021-2023"
      />
    </div>
  );
};



const Img = ({ src, alt, showFull }) => {
  const breakPoint = useMediaQuery(1114);
  return (
    <img
      height={480}
      width={112}
      className={`h-[30rem] rounded-xl w-28 ${
        !breakPoint ? "hover:w-[50rem]" : ""
      } ${
        breakPoint && showFull && "w-[50rem]"
      } transition-all duration-500 ease-in-out object-cover`}
      src={`/${src}`}
      fetchPriority="high"
      alt={alt}
    />
  );
};

const Gallery = () => {
  return (
    <div className=" my-24 flex flex-row gap-3 justify-center items-center overflow-clip ">
      <Img src={"coorg-view.jpg"} alt={"coorg-view"} />
      <Img
        src={"coffee-plantation.jpg"}
        alt={"coffee-plantation"}
        showFull={true}
      />
      <Img src={"me.jpg"} alt={"myself"} />
    </div>
  );
};

const About = () => {
  let heroAnimation = useSlideAnimation(0.2, "big");
  let sub1 = useSlideAnimation(0.4, "small");
  let sub2 = useSlideAnimation(0.6, "small");
  return (
    <div className="flex flex-col gap-2 justify-center items-center px-0 lg:px-20">
      <Hero hero={["Hello World!"]} hideStar={true} />
      <div className="flex md:flex-row flex-col gap-10 md:gap-24 mt-6 md:mt-14 w-[100%] items-center place-content-center">
        <div
          style={heroAnimation}
          className=" flex flex-col justify-center items-center w-full md:w-[30%]"
        >
          <div className="relative h-[18.25rem] text-[#ffbf73]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-[20rem] stroke-[#000000] dark:stroke-[#ffffff] overflow-visible"
              fill="currentColor"
              viewBox="0 0 24 24"
              stroke="black"
              strokeWidth={0.2}
              strokeLinecap="round"
            >
              <path d="M24 22h-24l12-20z" />
            </svg>
            <img
              src="/pvbny.png"
              width={1200}
              height={1200}
              className="left-[1%] z-30 absolute bottom-0"
              fetchPriority="high"
              alt="hero-img"
            />
          </div>
        </div>
        <div className="flex flex-col justify-center items-center z-30 w-full md:w-[50%] px-8 md:px-2">
          <h2
            style={heroAnimation}
            className="hero2 text-light-primary-text dark:text-dark-primary-text leading-[110%] font-bold font-hero flex justify-center text-left"
          >
            I'm a Software Engineer working from Raleigh, North Carolina, USA.
          </h2>
          <p
            style={sub1}
            className="font-body text-light-secondary-text dark:text-dark-secondary-text mt-14"
          >
            Hi, I'm Prabhanjan Vinoda Bharadwaj. Throughout the last three years, I've delved
            into diverse realms encompassing web development, backend design,
            and data engineering. Embracing versatility, I've taken on
            numerous roles.
          </p>
          <p
            style={sub2}
            className="font-body text-light-secondary-text dark:text-dark-secondary-text mt-8"
          >
            Currently, I'm employed as a Software Engineer Intern at
            LexisNexis. I have assumed the role of a Full-Stack Developer, delivering features and
            shipping fast for a legal AI product called Lexis Practical Guidance. 
          </p>
          <p
            style={sub1}
            className="font-body text-light-secondary-text dark:text-dark-secondary-text mt-14"
          >
            I worked for 2 years in HPE Storage R&D, as a Cloud Developer where I deployed analytics solutions
            into big data engineering pipelines. I dealt with microservices, database internals and AI/ML projects
            to enhance my understanding of how technology can drive businesses in the world.

          </p>
        </div>
      </div>
      <div className="h-auto w-full bg-light-primary dark:bg-dark-primary  my-20 mt-28 lg:rounded-[6rem] p-16 md:p-20 lg:p-40 flex flex-col gap-16 md:gap-24">
        <div className="w-full flex flex-col md:flex-row gap-16 lg:gap-28 h-auto">
          <PlanCard
            number="01"
            title="Strategize"
            content="Drawing on my expertise in high-level and low-level design, I conduct thorough analyses of project requirements, define clear objectives, and create strategic plans. This ensures a strong foundation for developing scalable, maintainable, and effective software solutions."
          />
          <PlanCard
            number="02"
            title="Architect"
            content="In this phase, I design comprehensive systems, craft intuitive user interfaces, and develop efficient algorithms. My focus is on creating high-performance, scalable, and maintainable solutions, integrating both frontend and backend technologies seamlessly."
          />
        </div>
        <div className="w-full flex flex-col md:flex-row gap-16 md:gap-28 h-auto">
          <PlanCard
            number="03"
            title="Implement"
            content="During the implementation phase, I engage deeply in coding, using a blend of frontend and backend technologies to meticulously develop features. I rigorously test components and collaborate with team members to deliver a cohesive and efficient software product."
          />
          <PlanCard
            number="04"
            title="Launch"
            content="Overseeing the launch process, I perform extensive testing, solicit and incorporate feedback, and ensure smooth integration and operation. My approach emphasizes continuous improvement and proactive support to maintain the software’s relevance and efficiency post-launch."
          />
        </div>
      </div>
      <Work />
      <div className="flex flex-col justify-center items-center z-30 w-full md:w-[50%] px-8 md:px-2">
        <p className="font-body text-light-secondary-text dark:text-dark-secondary-text mt-14">
        Born and raised during a time of unprecedented technological growth, I developed a natural enthusiasm for understanding the 
        intricate science behind computers and their profound impact on our lives. My fascination with machines 
        and their role in shaping human progress led me to delve into electronics and communication, where I 
        found joy in learning the fundamentals of electronic systems and computer programming.
        </p>
        <p className="font-body text-light-secondary-text dark:text-dark-secondary-text mt-8">
        My journey into software development began in earnest during my undergraduate studies. Engaging in projects 
        that ranged from programming microcontrollers to developing complex algorithms, I realized my passion 
        for software engineering. This passion guided me to a rewarding role as an R&D Developer at 
        Hewlett Packard Enterprise, where I honed my skills in backend development, data engineering, 
        and high-performance computing.
        </p>
        <p className="font-body text-light-secondary-text dark:text-dark-secondary-text mt-8">
        Beyond my professional pursuits, I have always valued the importance of empirical learning and leadership. 
        As a placement secretary in college, I helped my peers secure their dream jobs, and as a volunteer 
        with Make A Difference (MAD), I taught underprivileged students, fostering their academic growth.
        </p>
      </div>
      <Gallery />
    </div>
  );
};
export default About;
